/**
 * Control de arqueos — pestaña de Caja.
 *
 * Tablero donde el dueño revisa los arqueos de cierre de todas sus sucursales y
 * marca los que ya controló. Se muestra solo con
 * `organizations.enable_arqueo_review` activo y solo a un administrador (la
 * regla vive en utils/arqueoReview.js y la refuerza el trigger
 * `tr_turnos_caja_guard_control` en la base).
 *
 * Por qué es una pestaña aparte y no una columna del "Historial interno":
 *  - el historial trae los últimos 50 cierres de la organización sin filtro de
 *    sucursal; con cinco sucursales son dos semanas y no sirve para controlar;
 *  - el historial lo abre quien tenga `caja-cierre-completo`, que no es lo mismo
 *    que poder aprobar un arqueo.
 *
 * Click en un arqueo abre su detalle (CajaDetalle) para revisarlo antes de
 * aprobarlo; desde ahí también se marca. Los filtros y la página se guardan en
 * sessionStorage para que "Volver" deje al dueño donde estaba.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Check, CheckCircle2, Clock, Eye, RefreshCcw, Store, Undo2, User } from 'lucide-react';
import toast from 'react-hot-toast';

import { supabase } from '../../supabase';
import { useSite } from '../../context/SiteContext';
import { CONTROL_ESTADO, buildRangoControl, entraAlCorte, esAutocontrol } from '../../utils/arqueoReview';
import { formatOperationDateTime } from '../../utils/localDateTime';
import ControlArqueoModal from './ControlArqueoModal';

const PAGE_SIZE = 25;

const FILTROS_KEY = (organizationId) => `impulza:control-arqueos:${organizationId || 'sin-org'}`;
const ESTADOS_VALIDOS = new Set([CONTROL_ESTADO.PENDIENTE, CONTROL_ESTADO.CONTROLADO, 'todos']);

/** Filtros guardados de la visita anterior. Nunca rompe: sin storage, defaults. */
const leerFiltros = (organizationId) => {
  const base = { estado: CONTROL_ESTADO.PENDIENTE, sitioId: '', desde: '', hasta: '', page: 0 };
  try {
    const raw = window.sessionStorage.getItem(FILTROS_KEY(organizationId));
    if (!raw) return base;
    const guardado = JSON.parse(raw) || {};
    return {
      estado: ESTADOS_VALIDOS.has(guardado.estado) ? guardado.estado : base.estado,
      sitioId: typeof guardado.sitioId === 'string' ? guardado.sitioId : '',
      desde: typeof guardado.desde === 'string' ? guardado.desde : '',
      hasta: typeof guardado.hasta === 'string' ? guardado.hasta : '',
      page: Number.isInteger(guardado.page) && guardado.page >= 0 ? guardado.page : 0,
    };
  } catch {
    return base;
  }
};

const CAMPOS = [
  'id',
  'sitio_id',
  'estado',
  'fecha_apertura',
  'fecha_cierre',
  'saldo_inicial',
  'total_real',
  'diferencia',
  'usuario_email',
  'observaciones_cierre',
  'controlado_at',
  'controlado_por',
  'controlado_por_email',
  'control_observacion',
  'control_historial',
].join(', ');

/**
 * Aspecto de la fila según su estado de control. Se pinta la fila entera (borde
 * izquierdo + fondo) y no solo un texto: en el filtro "Todos" el dueño tiene
 * que distinguir de un vistazo qué ya revisó.
 *  - controlado: verde.
 *  - pendiente (dentro del corte): ámbar, es lo que falta revisar.
 *  - anterior al corte: gris neutro. No es pendiente (no suma al contador), así
 *    que pintarlo ámbar mentiría.
 * Un turno anterior al corte que igual se marcó cuenta como controlado: manda
 * `controlado_at`, que es la única fuente de verdad.
 */
const ESTILO_ESTADO = {
  controlado: {
    fila: 'border-emerald-200 border-l-4 border-l-emerald-500 bg-emerald-50/60 hover:border-emerald-300',
    badge: 'bg-emerald-100 text-emerald-700',
    label: 'Controlado',
    Icono: CheckCircle2,
  },
  pendiente: {
    fila: 'border-slate-100 border-l-4 border-l-amber-400 bg-white hover:border-slate-200',
    badge: 'bg-amber-50 text-amber-700',
    label: 'Pendiente',
    Icono: Clock,
  },
  fueraDeCorte: {
    fila: 'border-slate-100 border-l-4 border-l-slate-200 bg-white hover:border-slate-200',
    badge: 'bg-slate-100 text-slate-500',
    label: 'Anterior al control',
    Icono: null,
  },
};

const estiloDe = (turno, organization) => {
  if (turno?.controlado_at) return ESTILO_ESTADO.controlado;
  return entraAlCorte(turno, organization) ? ESTILO_ESTADO.pendiente : ESTILO_ESTADO.fueraDeCorte;
};

const money = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const numero = Number(value);
  if (!Number.isFinite(numero)) return '—';
  return numero.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/** El mayor de dos bordes inferiores: el corte del lógico y el filtro del usuario. */
const mayorFecha = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  return new Date(a) >= new Date(b) ? a : b;
};

function ControlArqueosPanel({ organization = null, profile = null, onPendientesChange, onVerDetalle }) {
  const { sites } = useSite();

  const corte = organization?.arqueo_review_since || null;
  const [filtrosIniciales] = useState(() => leerFiltros(profile?.organization_id));

  const [estado, setEstado] = useState(filtrosIniciales.estado);
  const [sitioId, setSitioId] = useState(filtrosIniciales.sitioId);
  const [desde, setDesde] = useState(filtrosIniciales.desde);
  const [hasta, setHasta] = useState(filtrosIniciales.hasta);
  const [page, setPage] = useState(filtrosIniciales.page);

  const [arqueos, setArqueos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { turno, accion: 'marcar'|'deshacer' }

  const nombreSitio = useCallback(
    (id) => {
      if (!id) return 'Sin sucursal';
      return sites.find((s) => String(s.id) === String(id))?.nombre || 'Sucursal dada de baja';
    },
    [sites],
  );

  const fetchArqueos = useCallback(async () => {
    if (!profile?.organization_id) return;
    setLoading(true);

    try {
      const rango = buildRangoControl({ desde, hasta });
      // Los pendientes arrancan en el corte del lógico: sin esto, activar la
      // función en una empresa con años de historia deja cientos de arqueos
      // "pendientes" que nadie va a revisar.
      const limiteInferior =
        estado === CONTROL_ESTADO.PENDIENTE ? mayorFecha(corte, rango.gte) : rango.gte;

      let query = supabase
        .from('turnos_caja')
        .select(CAMPOS, { count: 'exact' })
        .eq('organization_id', profile.organization_id)
        .eq('estado', 'Cerrado')
        .order('fecha_cierre', { ascending: false, nullsFirst: false })
        .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      if (sitioId) query = query.eq('sitio_id', sitioId);
      if (limiteInferior) query = query.gte('fecha_cierre', limiteInferior);
      if (rango.lte) query = query.lte('fecha_cierre', rango.lte);
      if (estado === CONTROL_ESTADO.PENDIENTE) query = query.is('controlado_at', null);
      if (estado === CONTROL_ESTADO.CONTROLADO) query = query.not('controlado_at', 'is', null);

      const { data, error, count } = await query;
      if (error) throw error;

      setArqueos(data || []);
      setTotal(count || 0);
    } catch (error) {
      console.error('No se pudieron cargar los arqueos a controlar:', error);
      toast.error('No se pudieron cargar los arqueos.');
      setArqueos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [corte, desde, estado, hasta, page, profile?.organization_id, sitioId]);

  /**
   * Contador del tab: pendientes de TODA la organización, sin los filtros de la
   * vista. Va con head:true para no traer filas y apoya en el índice parcial
   * idx_turnos_caja_control_pendientes.
   */
  const fetchPendientes = useCallback(async () => {
    if (!profile?.organization_id || !onPendientesChange) return;
    if (!corte) {
      onPendientesChange(0);
      return;
    }

    const { count, error } = await supabase
      .from('turnos_caja')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', profile.organization_id)
      .eq('estado', 'Cerrado')
      .is('controlado_at', null)
      .gte('fecha_cierre', corte);

    if (error) {
      console.error('No se pudo contar los arqueos pendientes:', error);
      return;
    }
    onPendientesChange(count || 0);
  }, [corte, onPendientesChange, profile?.organization_id]);

  useEffect(() => { fetchArqueos(); }, [fetchArqueos]);
  useEffect(() => { fetchPendientes(); }, [fetchPendientes]);

  // Cambiar un filtro vuelve a la primera página: si no, se ve "no hay
  // resultados" estando en la página 4 de un filtro que ya no existe. El primer
  // render no cuenta: ahí se está restaurando la página guardada.
  const filtrosMontados = useRef(false);
  useEffect(() => {
    if (!filtrosMontados.current) {
      filtrosMontados.current = true;
      return;
    }
    setPage(0);
  }, [estado, sitioId, desde, hasta]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        FILTROS_KEY(profile?.organization_id),
        JSON.stringify({ estado, sitioId, desde, hasta, page }),
      );
    } catch {
      // Sin storage (modo privado, bloqueado): se pierde solo la comodidad.
    }
  }, [desde, estado, hasta, page, profile?.organization_id, sitioId]);

  // Volver a una página que ya no existe (se controlaron los últimos de la
  // última página) muestra la anterior en vez de una lista vacía.
  useEffect(() => {
    if (!loading && page > 0 && arqueos.length === 0 && total > 0) {
      setPage(Math.max(0, Math.ceil(total / PAGE_SIZE) - 1));
    }
  }, [arqueos.length, loading, page, total]);

  const alTerminarControl = async () => {
    setModal(null);
    await Promise.all([fetchArqueos(), fetchPendientes()]);
  };

  const paginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hayFiltroFecha = Boolean(desde || hasta);

  const resumen = useMemo(() => {
    if (loading) return 'Cargando…';
    if (!total) return 'Sin arqueos para este filtro';
    const desdeFila = page * PAGE_SIZE + 1;
    const hastaFila = Math.min(total, (page + 1) * PAGE_SIZE);
    return `${desdeFila}–${hastaFila} de ${total}`;
  }, [loading, page, total]);

  return (
    <div className="p-4 sm:p-6">
      {!corte && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-[10px] font-black uppercase tracking-tight text-amber-800">
            Todavía no hay fecha de control
          </p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-amber-700">
            Guardá una vez el lógico en Datos de la empresa para fijar desde cuándo se controlan los arqueos.
          </p>
        </div>
      )}

      {/* FILTROS */}
      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-end">
        <div className="flex items-center gap-1 rounded-xl border border-slate-100 bg-slate-50 p-1">
          {[
            { id: CONTROL_ESTADO.PENDIENTE, label: 'Pendientes' },
            { id: CONTROL_ESTADO.CONTROLADO, label: 'Controlados' },
            { id: 'todos', label: 'Todos' },
          ].map((opcion) => (
            <button
              key={opcion.id}
              type="button"
              onClick={() => setEstado(opcion.id)}
              className={`rounded-lg px-3 py-2 text-[9px] font-black uppercase tracking-widest transition-all ${
                estado === opcion.id
                  ? 'border border-slate-100 bg-white text-[#0B2A36] shadow-sm'
                  : 'text-slate-400 hover:text-slate-600'
              }`}
            >
              {opcion.label}
            </button>
          ))}
        </div>

        <select
          value={sitioId}
          onChange={(e) => setSitioId(e.target.value)}
          className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 outline-none focus:border-[#51A7FD]"
        >
          <option value="">Todas las sucursales</option>
          {sites.map((site) => (
            <option key={site.id} value={site.id}>{site.nombre}</option>
          ))}
        </select>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 outline-none focus:border-[#51A7FD]"
          />
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">a</span>
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-600 outline-none focus:border-[#51A7FD]"
          />
          {hayFiltroFecha && (
            <button
              type="button"
              onClick={() => { setDesde(''); setHasta(''); }}
              className="text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600"
            >
              Limpiar
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 lg:ml-auto">
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">{resumen}</span>
          <button
            type="button"
            onClick={() => { fetchArqueos(); fetchPendientes(); }}
            className="rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-slate-500 transition-all hover:text-[#0B2A36]"
            title="Actualizar"
          >
            <RefreshCcw size={12} />
          </button>
        </div>
      </div>

      {/* LISTA */}
      {loading ? (
        <p className="py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">
          Cargando arqueos…
        </p>
      ) : arqueos.length === 0 ? (
        <p className="py-10 text-center text-[10px] font-black uppercase tracking-widest text-slate-400">
          {estado === CONTROL_ESTADO.PENDIENTE
            ? 'No hay arqueos pendientes de control'
            : 'No hay arqueos para este filtro'}
        </p>
      ) : (
        <div className="space-y-2">
          {arqueos.map((turno) => {
            const controlado = Boolean(turno.controlado_at);
            const propio = esAutocontrol(turno);
            const estilo = estiloDe(turno, organization);
            const IconoEstado = estilo.Icono;

            return (
              <div
                key={turno.id}
                role={onVerDetalle ? 'button' : undefined}
                tabIndex={onVerDetalle ? 0 : undefined}
                onClick={onVerDetalle ? () => onVerDetalle(turno.id) : undefined}
                onKeyDown={
                  onVerDetalle
                    ? (e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onVerDetalle(turno.id);
                        }
                      }
                    : undefined
                }
                title={onVerDetalle ? 'Ver el detalle del arqueo' : undefined}
                className={`rounded-xl border p-3 transition-all ${estilo.fila} ${
                  onVerDetalle ? 'cursor-pointer focus:outline-none focus-visible:border-[#51A7FD]' : ''
                } ${onVerDetalle && !controlado ? 'hover:bg-slate-50/60' : ''}`}
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[9px] font-black uppercase tracking-widest ${estilo.badge}`}
                      >
                        {IconoEstado && <IconoEstado size={10} />}
                        {estilo.label}
                      </span>
                      <span className="text-[11px] font-black uppercase tracking-tight text-[#0B2A36]">
                        {formatOperationDateTime(turno.fecha_cierre)}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[9px] font-black uppercase tracking-widest text-slate-500 ${controlado ? 'bg-white/70' : 'bg-slate-50'}`}>
                        <Store size={10} />
                        {nombreSitio(turno.sitio_id)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                        <User size={10} />
                        {turno.usuario_email || 'Sin usuario'}
                      </span>
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-3 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                      <span>Inicial $ {money(turno.saldo_inicial)}</span>
                      <span>Declarado $ {money(turno.total_real)}</span>
                      <span
                        className={
                          Number(turno.diferencia || 0) === 0
                            ? 'text-slate-400'
                            : Number(turno.diferencia) < 0
                              ? 'text-rose-500'
                              : 'text-emerald-600'
                        }
                      >
                        Diferencia $ {money(turno.diferencia)}
                      </span>
                    </div>

                    {controlado && (
                      <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-emerald-600">
                        Controlado por {turno.controlado_por_email || 'usuario borrado'} ·{' '}
                        {formatOperationDateTime(turno.controlado_at)}
                        {propio && ' · lo controló quien cerró el turno'}
                      </p>
                    )}
                    {controlado && turno.control_observacion && (
                      <p className="mt-1 text-[10px] font-bold text-slate-500">“{turno.control_observacion}”</p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {onVerDetalle && (
                      <button
                        type="button"
                        onClick={() => onVerDetalle(turno.id)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500 transition-all hover:text-[#0B2A36]"
                      >
                        <Eye size={12} />
                        Ver detalle
                      </button>
                    )}
                    {controlado ? (
                      <button
                        type="button"
                        onClick={() => setModal({ turno, accion: 'deshacer' })}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-slate-500 transition-all hover:text-[#0B2A36]"
                      >
                        <Undo2 size={12} />
                        Deshacer
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setModal({ turno, accion: 'marcar' })}
                        className="inline-flex items-center gap-2 rounded-xl bg-[#0B2A36] px-4 py-2.5 text-[9px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:brightness-110 disabled:opacity-50"
                      >
                        <Check size={12} />
                        Marcar controlado
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* PAGINACIÓN */}
      {paginas > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={page === 0 || loading}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-slate-500 disabled:opacity-40"
          >
            <ChevronLeft size={12} />
          </button>
          <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
            Página {page + 1} de {paginas}
          </span>
          <button
            type="button"
            disabled={page + 1 >= paginas || loading}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-slate-500 disabled:opacity-40"
          >
            <ChevronRight size={12} />
          </button>
        </div>
      )}

      {/* MODAL */}
      {modal?.turno && (
        <ControlArqueoModal
          turno={modal.turno}
          accion={modal.accion}
          profile={profile}
          sitioNombre={nombreSitio(modal.turno.sitio_id)}
          onClose={() => setModal(null)}
          onDone={alTerminarControl}
        />
      )}
    </div>
  );
}

export default ControlArqueosPanel;
